import { memo, MutableRefObject, useEffect } from 'react'
import clsx from 'clsx'
import { styled } from '@mui/material'
import { DISABLE_SELECTION_CLASS } from '$setup/style/constants'
import { isImageCached, loadImage } from '$func/load-image'
import { useStateRef } from '$hooks/use-state-ref'
import useEffectWithPrevDeps from '$hooks/use-effect-with-prev-deps'

interface Props {
  isLoading: boolean
  thumbnail?: string
  fileUrl?: string
  onLoadingStarted: () => unknown
  onLoadingEnded: () => unknown
  onViewRef: MutableRefObject<() => unknown>
  isUploading: boolean
}

export const StoryImage = memo(function StoryImageComponent({
  isLoading,
  thumbnail,
  fileUrl,
  onLoadingStarted,
  onLoadingEnded,
  onViewRef,
  isUploading,
}: Props) {
  const src = isLoading ? thumbnail : fileUrl
  const isLoadingRef = useStateRef(isLoading)
  const fileUrlRef = useStateRef(fileUrl)

  useEffectWithPrevDeps(
    ([prevFileUrl]) => {
      if (!fileUrl) return
      if (isLoadingRef.current && prevFileUrl === fileUrl) {
        return
      }

      if (isImageCached(fileUrl)) {
        if (!isUploading) onViewRef.current?.()
        onLoadingEnded()
        return
      }
      onLoadingStarted()
      loadImage(fileUrl).then(() => {
        if (fileUrlRef.current !== fileUrl) return

        if (!isUploading) onViewRef.current?.()
        onLoadingEnded()
      })
    },
    [fileUrl, onLoadingStarted, onLoadingEnded, isUploading],
  )

  return (
    <StyledImage
      className={clsx(DISABLE_SELECTION_CLASS, {
        'apply-filter': isLoading,
      })}
      src={src}
      alt=""
      draggable={false}
    />
  )
})

const StyledImage = styled('img')`
  width: 100%;
  height: 100%;
  object-fit: contain;
  border: 0;
  &.apply-filter {
    filter: blur(10px);
  }
`
